<template>
  <!--
    AB#21966 — Order chats in the app bar: every order team chat this person
    follows with something unread, mentions first-class. Clicking one opens the
    order with its Team chat showing.
  -->
  <div class="tcb-wrap">
    <button
      type="button"
      class="mb-btn"
      :class="{ 'mb-btn--unread': unread > 0 }"
      title="Order chats"
      :aria-label="unread ? `Order chats, ${unread} unread` : 'Order chats'"
      :aria-expanded="open"
      data-test="team-chat-bell"
      @click="toggle"
    >
      <ChatBubbleLeftRightIcon class="mb-ic" aria-hidden="true" />
      <span
        v-if="unread > 0"
        class="mb-n"
        :class="{ 'tcb-n--mention': mentions > 0 }"
        data-test="team-chat-unread"
        >{{ unread > 99 ? '99+' : unread }}</span
      >
    </button>
    <Teleport to="body">
      <div v-if="open" class="tcb-scrim" @click="open = false"></div>
      <div
        v-if="open"
        class="tcb-pop"
        role="dialog"
        aria-label="Order chats"
        data-test="team-chat-pop"
      >
        <div class="tcb-head">
          Order chats
          <span class="tcb-tabs" role="tablist">
            <button
              type="button"
              role="tab"
              class="tcb-tab"
              :class="{ 'tcb-tab--on': view === 'all' }"
              :aria-selected="view === 'all'"
              @click="view = 'all'"
            >
              Unread · {{ unread }}
            </button>
            <button
              type="button"
              role="tab"
              class="tcb-tab"
              :class="{ 'tcb-tab--on': view === 'mentions' }"
              :aria-selected="view === 'mentions'"
              data-test="team-chat-mentions-tab"
              @click="view = 'mentions'"
            >
              Mentions · {{ mentions }}
            </button>
          </span>
        </div>
        <div v-if="!items.length" class="tcb-empty">
          {{ view === 'mentions' ? 'Nobody is waiting on you.' : 'You are all caught up.' }}
        </div>
        <button
          v-for="it in items"
          :key="it.order_id"
          type="button"
          class="tcb-it tcb-it--unread"
          :data-test="`team-chat-item-${it.order_id}`"
          @click="go(it)"
        >
          <AgentAvatar v-if="it.last?.kind === 'elsa'" agent="elsa" :size="34" class="tcb-ic2" />
          <UserAvatar
            v-else
            :name="it.last?.by"
            :initials="it.last?.initials"
            :src="it.last?.avatar_url ?? null"
            :size="34"
            class="tcb-ic2"
          />
          <span class="tcb-txt">
            <b
              >#{{ it.order_no
              }}<span v-if="it.company" class="tcb-co"> · {{ it.company }}</span></b
            >
            <p>
              <strong>{{ it.last?.by }}:</strong> {{ it.last?.text }}
            </p>
            <span class="tcb-badges">
              <span v-if="it.mentions" class="tcb-badge tcb-badge--at">@ you</span>
              <span class="tcb-badge">{{ it.unread }} unread</span>
            </span>
          </span>
          <span class="tcb-ts">{{ ago(it.last?.at) }}</span>
        </button>
        <div class="tcb-foot">
          Away for {{ emailMinutes }} minutes? You get one email per chat, not one per message.
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ChatBubbleLeftRightIcon } from '@heroicons/vue/24/outline';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import { goToDeskTarget } from '@/composables/useDeskMessages';
import { ago, inbox, type TcInboxItem } from '@/composables/useTeamChat';
import { syncAppBadge } from '@/composables/usePushNotifications';

const props = withDefaults(defineProps<{ pollMs?: number }>(), { pollMs: 60000 });
const router = useRouter();
const route = useRoute();
const open = ref(false);
const view = ref<'all' | 'mentions'>('all');
const emailMinutes = 15;

const unread = computed(() => inbox.state.unread);
// AB#22138 — the app icon's number follows the unread count.
watch(unread, (n) => syncAppBadge(n));
const mentions = computed(() => inbox.state.mentions);
const items = computed(() =>
  view.value === 'mentions' ? inbox.state.items.filter((i) => i.mentions > 0) : inbox.state.items
);

const toggle = async (): Promise<void> => {
  open.value = !open.value;
  if (open.value) await inbox.load();
};
const go = (it: TcInboxItem): void => {
  open.value = false;
  if (!it.order_item_id) return;
  goToDeskTarget(router, route.name, {
    name: 'dashboard',
    query: {
      order_id: String(it.order_id),
      order_item_id: String(it.order_item_id),
      order_no: it.order_no,
      tab: 'team_chat',
    },
  });
};

onMounted(() => inbox.start(props.pollMs));
onBeforeUnmount(() => inbox.stop());
</script>

<style scoped>
.tcb-wrap {
  position: relative;
}
/* Same pill as the Messages bell (MessagesBell.vue). */
.mb-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.08);
  color: #cfd4da;
  cursor: pointer;
  transition: background-color 0.12s ease;
}
.mb-btn:hover {
  background: rgba(255, 255, 255, 0.14);
}
.mb-btn--unread {
  background: rgba(79, 124, 240, 0.35);
  color: #fff;
}
.mb-ic {
  width: 18px;
  height: 18px;
}
.mb-n {
  position: absolute;
  top: -5px;
  right: -4px;
  background: var(--accent);
  color: #fff;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
}
.tcb-n--mention {
  background: #c0392b;
}
</style>
<style>
/* Teleported to body: unscoped, token-driven (the Messages bell's popover, own names). */
.tcb-scrim {
  position: fixed;
  inset: 0;
  z-index: 10045;
}
.tcb-pop {
  position: fixed;
  right: 260px;
  top: calc(var(--app-bar-offset, 53px) + 6px);
  width: min(94vw, 420px);
  max-height: calc(100vh - var(--app-bar-offset, 53px) - 24px);
  overflow: auto;
  background: var(--surface);
  color: var(--ink);
  border: 1px solid var(--border);
  border-radius: 12px;
  box-shadow: var(--shadow-2, 0 20px 50px rgba(0, 0, 0, 0.3));
  z-index: 10046;
}
@media (max-width: 900px) {
  .tcb-pop {
    right: 3vw;
    left: 3vw;
    width: auto;
  }
}
.tcb-head {
  padding: 12px 16px;
  border-bottom: 1px solid var(--border);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}
.tcb-empty {
  padding: 18px 16px;
  color: var(--ink-mute);
  font-size: 13px;
}
.tcb-it {
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid var(--border-soft);
  background: transparent;
  color: var(--ink);
  display: flex;
  gap: 12px;
  cursor: pointer;
}
.tcb-it:hover,
.tcb-it--unread {
  background: var(--surface-2);
}
.tcb-ic2 {
  flex: none;
}
.tcb-txt {
  min-width: 0;
  flex: 1;
}
.tcb-txt b {
  font-size: 13.5px;
  display: block;
  overflow-wrap: anywhere;
}
.tcb-txt p {
  margin: 2px 0 0;
  font-size: 12.5px;
  color: var(--ink-soft);
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.tcb-ts {
  font-size: 11.5px;
  color: var(--ink-mute);
  white-space: nowrap;
}
.tcb-tabs {
  margin-left: auto;
  display: inline-flex;
  gap: 4px;
}
.tcb-tab {
  border: 1px solid var(--border);
  background: transparent;
  color: var(--ink-soft);
  font-size: 11.5px;
  font-weight: 500;
  border-radius: 7px;
  padding: 3px 8px;
  cursor: pointer;
}
.tcb-tab--on {
  background: var(--accent);
  border-color: var(--accent);
  color: #fff;
}
.tcb-co {
  font-weight: 400;
  color: var(--ink-mute);
}
.tcb-badges {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}
.tcb-badge {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--accent);
  background: var(--accent-soft);
  border-radius: 5px;
  padding: 1px 6px;
}
.tcb-badge--at {
  color: var(--warning);
  background: var(--warning-soft);
}
.tcb-foot {
  padding: 10px 16px;
  font-size: 11.5px;
  color: var(--ink-mute);
}
</style>
